import type { CoinStruct } from '@iota/iota-sdk/client';
import { IotaClient } from '@iota/iota-sdk/client';
import { Transaction } from '@iota/iota-sdk/transactions';
import type { WalletAccount } from '@wallet-standard/core';
import BigNumber from 'bignumber.js';

import { getAllCoinsOfType } from './getAllCoinsOfType';

export const ERROR_COIN_METADATA_NOT_FUND = new Error(
  'Coin Metadata could not be found.',
);
export const ERROR_NOT_ENOUGH_BALANCE = new Error('Not enough balance.');

interface IPrepareCoinsParams {
  walletAccount: WalletAccount;
  client: IotaClient;
  amount: BigNumber;
  coinType: string;
}

export async function prepareCoinsTransaction({
  walletAccount,
  client,
  amount,
  coinType,
}: IPrepareCoinsParams) {
  const coins = await getAllCoinsOfType({ walletAccount, client, coinType });

  const transaction = new Transaction();

  const preparedCoins = await (async () => {
    const coinMetadata = await client.getCoinMetadata({
      coinType,
    });

    if (!coinMetadata) {
      throw ERROR_COIN_METADATA_NOT_FUND;
    }

    const unstakeAmount = BigInt(
      amount
        .multipliedBy(new BigNumber(10).pow(coinMetadata.decimals))
        .toString(10),
    );

    const selectedCoins = [] as CoinStruct[];
    let selectedAmount = BigInt(0);

    for (const coin of coins) {
      if (selectedAmount + BigInt(coin.balance) < unstakeAmount) {
        selectedCoins.push(coin);
        selectedAmount += BigInt(coin.balance);
      } else if (selectedAmount + BigInt(coin.balance) === unstakeAmount) {
        selectedCoins.push(coin);

        const coinObjects = selectedCoins.map(coin =>
          transaction.object(coin.coinObjectId),
        );

        if (coinObjects.length > 1) {
          const [coin, ...rest] = coinObjects;
          transaction.mergeCoins(coin, rest);
          return coin;
        }

        return coinObjects[0];
      } else if (selectedAmount + BigInt(coin.balance) > unstakeAmount) {
        const remaining = unstakeAmount - selectedAmount;
        const [splitCoin] = transaction.splitCoins(
          transaction.object(coin.coinObjectId),
          [transaction.pure.u64(remaining)],
        );

        if (selectedCoins.length === 0) {
          return transaction.object(splitCoin);
        }

        transaction.mergeCoins(
          splitCoin,
          selectedCoins.map(coin => transaction.object(coin.coinObjectId)),
        );

        return transaction.object(splitCoin);
      }
    }
    throw ERROR_NOT_ENOUGH_BALANCE;
  })();

  return { transaction, preparedCoins };
}
